import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Loader2, Truck, ShieldCheck, ChevronLeft, Tag, Check, CreditCard, X, Star, RotateCcw, MapPin, Clock } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/common/Logo";
import { api } from "@/lib/api";
import { fmt, applyPromo } from "@/data/catalog";
import { getReservationSeconds, fmtClock } from "@/lib/timer";
import { toast } from "sonner";
import { track } from "@/components/common/Analytics";
import { trackEvent, checkoutId } from "@/lib/track";
import { PaymentIcons, AfterpayBadge } from "@/components/common/PaymentIcons";
import { StripeCardSection } from "@/components/checkout/StripeCardSection";

const STATE_MAP = {
  "new south wales": "NSW", victoria: "VIC", queensland: "QLD", "south australia": "SA",
  "western australia": "WA", tasmania: "TAS", "northern territory": "NT", "australian capital territory": "ACT",
};

const STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

const inputCls =
  "w-full rounded-xl border border-[#EADDD7] bg-white px-4 py-3 text-sm text-[#2C2B2C] placeholder:text-[#B6A99F] transition-colors focus:border-[#FBC4A8] focus:outline-none focus:ring-2 focus:ring-[#FBC4A8]/30";

const Field = ({ label, children, half }) => (
  <label className={`flex flex-col gap-1.5 ${half ? "sm:flex-1" : "w-full"}`}>
    <span className="text-xs font-medium text-[#6E6168]">{label}</span>
    {children}
  </label>
);

const STEPS = ["Bag", "Details", "Payment"];
function Progress() {
  return (
    <div data-testid="checkout-progress" className="flex items-center gap-2.5">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2.5">
          <span className={`text-[0.72rem] ${i === 1 ? "font-semibold text-[#2C2B2C]" : "text-[#B6A99F]"}`}>{label}</span>
          {i < STEPS.length - 1 && <span className="text-[#D8CCC2]">›</span>}
        </div>
      ))}
    </div>
  );
}

function HeaderCountdown() {
  const [secs, setSecs] = useState(getReservationSeconds);
  useEffect(() => {
    const t = setInterval(() => setSecs(getReservationSeconds()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span data-testid="checkout-header-countdown" className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF7F2] px-3 py-1.5 text-xs font-medium text-[#2C2B2C] ring-1 ring-[#FBD9C6]">
      <Clock className="h-3.5 w-3.5 text-[#E97C6A]" /> Reserved <span className="tabular-nums font-semibold">{fmtClock(secs)}</span>
    </span>
  );
}

const PayMethod = ({ id, active, onClick, icon, label, children }) => (
  <div data-testid={`pay-method-${id}`}
    className={`overflow-hidden rounded-xl border transition-colors ${active ? "border-[#FBC4A8] shadow-[0_10px_30px_-20px_rgba(251,196,168,0.9)]" : "border-[#EADDD7]"}`}>
    <button type="button" onClick={onClick}
      className={`flex w-full items-center gap-3 px-5 py-4 text-left transition-colors ${active ? "bg-[#FFF7F2]" : "bg-white hover:bg-[#FFFCFA]"}`}>
      <span className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 transition-colors ${active ? "border-[#FBC4A8]" : "border-[#D8CCC2]"}`}>
        {active && <span className="h-2.5 w-2.5 rounded-full bg-[#FBC4A8]" />}
      </span>
      <span className="flex-1 text-sm font-medium text-[#2C2B2C]">{label}</span>
      {icon}
    </button>
    {active && children && <div className="border-t border-[#FBC4A8]/50 bg-[#FFFCFA] px-5 py-5">{children}</div>}
  </div>
);

export default function Checkout() {
  const navigate = useNavigate();
  const { detailed, subtotal, items } = useCart();
  const base = applyPromo(subtotal);
  const [loading, setLoading] = useState("");
  const [paypal, setPaypal] = useState(null);
  const [method, setMethod] = useState("card");
  const [discount, setDiscount] = useState(null); // {code, amount_off}
  const [codeInput, setCodeInput] = useState("");
  const [codeState, setCodeState] = useState(""); // "", "checking", "ok", "err"
  const [codeMsg, setCodeMsg] = useState("");
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    line1: "", line2: "", suburb: "", state: "QLD", postcode: "",
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const [pubKey, setPubKey] = useState("");
  const [sugs, setSugs] = useState([]);
  const [showSugs, setShowSugs] = useState(false);
  const sugTimer = useRef(null);

  const formValid = !!(f.first_name && f.last_name && f.email && f.line1 && f.suburb && f.postcode);
  const ppOptions = useMemo(() => ({ clientId: paypal?.paypal_client_id, currency: "AUD", intent: "capture", components: "buttons" }), [paypal?.paypal_client_id]);

  const compareTotal = detailed.reduce((s, i) => s + (i.compareAt || i.price) * i.quantity, 0);
  const savings = Math.max(0, compareTotal - subtotal);
  const codeOff = discount?.amount_off || 0;
  const grandTotal = Math.max(0, base.total - codeOff);

  const deliveryBy = useMemo(
    () => new Date(Date.now() + 4 * 864e5).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" }),
    []
  );

  useEffect(() => {
    api.get("/site-config").then((r) => {
      const p = r.data?.payments;
      if (p?.paypal_enabled) setPaypal(p);
      if (p?.stripe_publishable_key) setPubKey(p.stripe_publishable_key);
    }).catch(() => {});
  }, []);

  // Free keyless address auto-suggest via OpenStreetMap (Photon)
  const onAddrChange = (e) => {
    const q = e.target.value;
    setF((p) => ({ ...p, line1: q }));
    clearTimeout(sugTimer.current);
    if (q.trim().length < 4) { setSugs([]); setShowSugs(false); return; }
    sugTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&lang=en&bbox=112.0,-44.0,154.0,-10.0`);
        const data = await res.json();
        const feats = (data.features || []).filter((ft) => (ft.properties?.countrycode === "AU" || ft.properties?.country === "Australia"));
        setSugs(feats.slice(0, 6));
        setShowSugs(feats.length > 0);
      } catch { setSugs([]); }
    }, 300);
  };

  const pickSug = (ft) => {
    const p = ft.properties || {};
    const street = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
    const suburb = p.city || p.district || p.locality || p.county || "";
    const st = STATE_MAP[(p.state || "").toLowerCase()] || f.state;
    setF((prev) => ({ ...prev, line1: street || prev.line1, suburb: suburb || prev.suburb, state: st, postcode: p.postcode || prev.postcode }));
    setShowSugs(false);
    setSugs([]);
  };

  useEffect(() => {
    if (items.length === 0) navigate("/cart", { replace: true });
  }, [items.length, navigate]);

  const validate = () => {
    if (!f.first_name || !f.last_name || !f.email || !f.line1 || !f.suburb || !f.postcode) {
      toast.error("Please fill in all required fields.");
      return false;
    }
    return true;
  };

  const skuItems = () => items.map((i) => ({ sku: i.sku, quantity: i.quantity }));

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeState("checking");
    try {
      const { data } = await api.post("/discount/validate", { code, items: skuItems() });
      if (data.valid) {
        setDiscount({ code: data.code, amount_off: data.amount_off });
        setCodeState("ok");
        setCodeMsg(data.message);
      } else {
        setDiscount(null);
        setCodeState("err");
        setCodeMsg(data.message || "That code isn't valid.");
      }
    } catch {
      setCodeState("err");
      setCodeMsg("Couldn't check that code. Please try again.");
    }
  };

  const clearCode = () => {
    setDiscount(null); setCodeInput(""); setCodeState(""); setCodeMsg("");
  };

  const basePayload = () => ({
    items: skuItems(),
    origin_url: window.location.origin,
    email: f.email || undefined,
    discount_code: discount?.code || undefined,
  });
  const customerPayload = () => ({
    ...basePayload(),
    email: f.email,
    customer: {
      first_name: f.first_name, last_name: f.last_name, email: f.email,
      phone: f.phone || null,
      address: { line1: f.line1, line2: f.line2 || null, suburb: f.suburb, state: f.state, postcode: f.postcode, country: "AU" },
    },
  });

  const fireInitiate = () => {
    track("InitiateCheckout", { value: grandTotal, currency: "AUD", num_items: items.length });
    trackEvent("initiate_checkout", { value: grandTotal });
  };
  const paymentFired = useRef(false);
  const firePayment = () => {
    if (paymentFired.current) return;
    paymentFired.current = true;
    track("AddPaymentInfo", { value: grandTotal, currency: "AUD" });
    trackEvent("payment_info", { value: grandTotal });
  };

  // Klaviyo "Checkout Started" — fires once as soon as a valid email is captured.
  const startedFired = useRef(false);
  useEffect(() => {
    if (startedFired.current || items.length === 0) return;
    const email = f.email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    startedFired.current = true;
    const csid = checkoutId();
    api.post("/checkout/started", {
      email,
      first_name: f.first_name || undefined,
      last_name: f.last_name || undefined,
      items: skuItems(),
      checkout_session_id: csid,
      checkout_url: window.location.origin + "/checkout",
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.email, f.first_name, f.last_name, items]);

  // Card + Afterpay both route to Stripe's secure page (which offers card & Afterpay in AU).
  const payWithStripe = async (withCustomer) => {
    setLoading(withCustomer ? "card" : "afterpay");
    fireInitiate();
    firePayment();
    try {
      const res = await api.post("/checkout/session", withCustomer ? customerPayload() : basePayload());
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      toast.error("Could not start checkout. Please try again.");
      setLoading("");
    }
  };

  const onPayNow = (e) => {
    e.preventDefault();
    if (method === "card") { if (!validate()) return; payWithStripe(true); }
    else if (method === "afterpay") { if (!validate()) return; payWithStripe(true); }
  };

  const createPaypalOrder = async () => { fireInitiate(); firePayment(); const res = await api.post("/paypal/create-order", basePayload()); return res.data.id; };
  const onPaypalApprove = async (data) => {
    try {
      await api.post(`/paypal/capture-order/${data.orderID}`);
      window.location.href = `${window.location.origin}/checkout/success?session_id=${data.orderID}&provider=paypal`;
    } catch { toast.error("PayPal payment could not be completed. Please try again."); }
  };

  if (items.length === 0) return null;

  const payPalButton = (
    <PayPalButtons
      style={{ layout: "horizontal", color: "gold", shape: "pill", label: "paypal", tagline: false, height: 48 }}
      createOrder={createPaypalOrder} onApprove={onPaypalApprove}
      onError={() => toast.error("PayPal is unavailable right now. Please try card checkout.")}
    />
  );

  const content = (
    <div className="min-h-screen bg-[#FFFCFA]" data-testid="checkout-page">
      {/* Minimal checkout header */}
      <header className="border-b border-[#F2E8E6] bg-white">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between px-5 py-4 md:px-8">
          <Logo to="/" testid="checkout-logo" />
          <span className="hidden sm:block"><Progress /></span>
          <HeaderCountdown />
        </div>
      </header>

      <div className="mx-auto max-w-[1180px] px-5 py-8 md:px-8 md:py-10">
        <Link to="/cart" data-testid="checkout-back" className="mb-6 inline-flex items-center gap-1.5 text-sm text-[#9A8E88] transition-colors hover:text-[#2C2B2C]">
          <ChevronLeft className="h-4 w-4" /> Back to bag
        </Link>

        {/* Delivery estimate — mobile only */}
        <div data-testid="checkout-delivery-estimate" className="mb-6 flex items-center gap-2.5 rounded-xl border border-[#CDE9DA] bg-[#EDF7F1] px-4 py-3 md:hidden">
          <span className="relative flex h-2.5 w-2.5 flex-none">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3B8C6E] opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#3B8C6E]" />
          </span>
          <p className="text-[0.82rem] leading-snug text-[#2C6B4F]">
            Complete your order today and get it by <span className="font-semibold text-[#2A5C43]">{deliveryBy}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 items-start gap-x-16 gap-y-10 lg:grid-cols-[1.1fr_0.9fr]">
          {/* LEFT */}
          <div className="min-w-0">
            {/* Express checkout */}
            <div data-testid="express-checkout">
              <p className="mb-3 text-center text-[0.68rem] font-medium uppercase tracking-[0.2em] text-[#9A8E88]">Express checkout</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {paypal ? (
                  <div data-testid="express-paypal" className="[&>div]:!min-h-[48px]">{payPalButton}</div>
                ) : (
                  <button type="button" disabled data-testid="express-paypal-disabled"
                    className="flex h-12 items-center justify-center rounded-full bg-[#FFC439] text-sm font-bold italic text-[#003087] opacity-60">PayPal</button>
                )}
                <button type="button" onClick={() => payWithStripe(false)} disabled={!!loading} data-testid="express-afterpay"
                  className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#B2FCE4] text-sm font-bold text-[#0A0A0A] transition-transform hover:scale-[1.01] disabled:opacity-60">
                  {loading === "afterpay" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pay with <span className="lowercase tracking-tight">afterpay</span></>}
                </button>
              </div>
              <div className="my-6 flex items-center gap-4 text-[0.68rem] uppercase tracking-[0.2em] text-[#B6A99F]">
                <span className="h-px flex-1 bg-[#EADDD7]" /> or <span className="h-px flex-1 bg-[#EADDD7]" />
              </div>
            </div>

            <form onSubmit={onPayNow} className="space-y-8">
              <section>
                <h2 className="mb-4 font-display text-xl text-[#2C2B2C]">Contact</h2>
                <div className="space-y-4">
                  <Field label="Email address *"><input data-testid="co-email" type="email" value={f.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" /></Field>
                  <Field label="Phone (for delivery updates)"><input data-testid="co-phone" value={f.phone} onChange={set("phone")} className={inputCls} placeholder="0400 000 000" /></Field>
                </div>
              </section>

              <section>
                <h2 className="mb-4 font-display text-xl text-[#2C2B2C]">Delivery</h2>
                <div className="space-y-4">
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Field label="First name *" half><input data-testid="co-first-name" value={f.first_name} onChange={set("first_name")} className={inputCls} placeholder="Jane" /></Field>
                    <Field label="Last name *" half><input data-testid="co-last-name" value={f.last_name} onChange={set("last_name")} className={inputCls} placeholder="Doe" /></Field>
                  </div>
                  <div className="relative">
                    <Field label="Street address *">
                      <input data-testid="co-line1" value={f.line1} onChange={onAddrChange} onFocus={() => sugs.length && setShowSugs(true)}
                        onBlur={() => setTimeout(() => setShowSugs(false), 150)} autoComplete="off" className={inputCls} placeholder="Start typing your address…" />
                    </Field>
                    {showSugs && sugs.length > 0 && (
                      <ul data-testid="address-suggestions" className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-[#EADDD7] bg-white shadow-lg">
                        {sugs.map((ft, i) => {
                          const p = ft.properties || {};
                          const line = [[p.housenumber, p.street || p.name].filter(Boolean).join(" "), p.city || p.district, p.state, p.postcode].filter(Boolean).join(", ");
                          return (
                            <li key={i}>
                              <button type="button" onMouseDown={(e) => { e.preventDefault(); pickSug(ft); }} data-testid={`address-sug-${i}`}
                                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm text-[#2C2B2C] transition-colors hover:bg-[#FFF7F2]">
                                <MapPin className="mt-0.5 h-4 w-4 flex-none text-[#FBC4A8]" /> {line}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <Field label="Apartment, suite, etc. (optional)"><input data-testid="co-line2" value={f.line2} onChange={set("line2")} className={inputCls} placeholder="Unit 4" /></Field>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Field label="Suburb *" half><input data-testid="co-suburb" value={f.suburb} onChange={set("suburb")} className={inputCls} placeholder="Gold Coast" /></Field>
                    <Field label="State *" half>
                      <select data-testid="co-state" value={f.state} onChange={set("state")} className={inputCls}>
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Postcode *" half><input data-testid="co-postcode" value={f.postcode} onChange={set("postcode")} className={inputCls} placeholder="4217" /></Field>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-xs text-[#6E6168]">
                    <Truck className="h-4 w-4 flex-none text-[#FBC4A8]" /> Free tracked express shipping · dispatched within 1 business day. Australia-wide only.
                  </div>
                </div>
              </section>

              {/* Payment */}
              <section data-testid="payment-section">
                <h2 className="font-display text-xl text-[#2C2B2C]">Payment</h2>
                <p className="mb-4 mt-1 text-sm text-[#9A8E88]">All transactions are secure and encrypted.</p>
                <div className="space-y-3">
                  <PayMethod id="card" active={method === "card"} onClick={() => setMethod("card")} label="Credit card"
                    icon={<PaymentIcons methods={["Visa", "Mastercard", "Amex"]} />}>
                    {pubKey ? (
                      <StripeCardSection publishableKey={pubKey} formValid={formValid} buildPayload={customerPayload} grandTotal={grandTotal} discountCode={discount?.code} onReady={firePayment} />
                    ) : (
                      <div className="flex items-start gap-3 text-sm text-[#6E6168]">
                        <CreditCard className="mt-0.5 h-5 w-5 flex-none text-[#FBC4A8]" />
                        <p>After you tap <span className="font-medium text-[#2C2B2C]">Pay now</span>, you'll enter your card details on our secure, encrypted Stripe payment page and return here once complete.</p>
                      </div>
                    )}
                  </PayMethod>
                  <PayMethod id="paypal" active={method === "paypal"} onClick={() => setMethod("paypal")} label="PayPal"
                    icon={<span className="text-sm font-bold italic"><span className="text-[#003087]">Pay</span><span className="text-[#0070E0]">Pal</span></span>}>
                    {paypal ? (
                      <div className="[&>div]:!min-h-[48px]">{payPalButton}</div>
                    ) : (
                      <p className="text-sm text-[#9A8E88]">PayPal isn't available right now. Please choose card or Afterpay.</p>
                    )}
                  </PayMethod>
                  <PayMethod id="afterpay" active={method === "afterpay"} onClick={() => setMethod("afterpay")} label="Afterpay"
                    icon={<AfterpayBadge />}>
                    <p className="text-sm text-[#6E6168]">Pay in 4 interest-free instalments of <span className="font-semibold text-[#2C2B2C]">{fmt(grandTotal / 4)}</span>. Tap Pay now to complete securely via Stripe.</p>
                  </PayMethod>
                </div>
              </section>

              {(method === "afterpay" || (method === "card" && !pubKey)) && (
                <button type="submit" disabled={!!loading} data-testid="co-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#FFFCFA] transition-all hover:bg-[#3a3636] hover:tracking-[0.2em] disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <><Lock className="h-3.5 w-3.5" /> Pay now · {fmt(grandTotal)}</>}
                </button>
              )}

              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-[#9A8E88]">
                <Lock className="h-3 w-3" /> Secure payment · encrypted &amp; processed by Stripe
              </p>
            </form>
          </div>

          {/* RIGHT — summary */}
          <div className="min-w-0 lg:sticky lg:top-8 lg:self-start">
            <div className="rounded-3xl border border-[#F2E8E6] bg-white p-5 shadow-[0_28px_70px_-46px_rgba(44,43,44,0.4)] md:p-7" data-testid="checkout-summary">
              <h2 className="font-display text-xl text-[#2C2B2C]">Order summary</h2>

              <div className="mt-5 space-y-5">
                {detailed.map((it) => (
                  <div key={it.sku} data-testid={`summary-row-${it.sku}`} className="flex items-center gap-4">
                    <div className="relative h-16 w-16 flex-none overflow-hidden rounded-xl border border-[#F2E8E6] bg-[#FFFCFA]">
                      <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                      <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#232222] text-[0.62rem] font-semibold text-white">{it.quantity}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[#2C2B2C]">{it.name}</p>
                      {it.type === "device" ? (
                        <p className="flex items-center gap-1.5 text-xs font-medium text-[#E97C6A]">
                          <span className="h-1.5 w-1.5 flex-none animate-pulse rounded-full bg-[#E97C6A]" /> Only 8 left in stock
                        </p>
                      ) : (
                        <p className="truncate text-xs text-[#9A8E88]">{it.subtitle}</p>
                      )}
                    </div>
                    <span className="text-sm font-medium text-[#2C2B2C]">{fmt(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>

              {/* Discount code */}
              <div className="mt-6 border-t border-[#F2E8E6] pt-6" data-testid="discount-box">
                {discount ? (
                  <div className="flex items-center justify-between rounded-xl border border-[#CDE9DA] bg-[#EDF7F1] px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-sm text-[#2C2B2C]"><Tag className="h-4 w-4 text-[#3B8C6E]" /> <span className="font-semibold">{discount.code}</span> applied</span>
                    <button type="button" onClick={clearCode} data-testid="discount-remove" className="rounded-full p-1 text-[#8A7F86] hover:bg-white"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input data-testid="discount-input" value={codeInput} onChange={(e) => { setCodeInput(e.target.value); setCodeState(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                      className={`${inputCls} flex-1`} placeholder="Discount code" />
                    <button type="button" onClick={applyCode} disabled={codeState === "checking" || !codeInput.trim()} data-testid="discount-apply"
                      className="flex-none rounded-xl bg-[#F2E8E6] px-5 text-sm font-medium text-[#2C2B2C] transition-colors hover:bg-[#EADDD7] disabled:opacity-50">
                      {codeState === "checking" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </button>
                  </div>
                )}
                {codeState === "err" && <p data-testid="discount-error" className="mt-2 text-xs text-[#b9433a]">{codeMsg}</p>}
                {codeState === "ok" && codeMsg && <p data-testid="discount-msg" className="mt-2 inline-flex items-center gap-1 text-xs text-[#3B8C6E]"><Check className="h-3 w-3" /> {codeMsg}</p>}
              </div>

              <div className="mt-6 space-y-3 border-t border-[#F2E8E6] pt-6 text-sm">
                <div className="flex justify-between text-[#6E6168]"><span>Subtotal</span><span data-testid="summary-subtotal">{fmt(compareTotal)}</span></div>
                {savings > 0 && (
                  <div className="flex items-center justify-between" data-testid="summary-savings-row">
                    <span className="inline-flex items-center gap-1.5 text-[#3B8C6E]"><Tag className="h-3.5 w-3.5" /> Savings</span>
                    <span className="font-medium text-[#3B8C6E]" data-testid="summary-savings">- {fmt(savings)}</span>
                  </div>
                )}
                {codeOff > 0 && (
                  <div className="flex items-center justify-between" data-testid="summary-code-row">
                    <span className="inline-flex items-center gap-1.5 text-[#3B8C6E]"><Tag className="h-3.5 w-3.5" /> Code · {discount.code}</span>
                    <span className="font-medium text-[#3B8C6E]">- {fmt(codeOff)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#6E6168]"><span>Shipping</span><span className="font-medium text-[#4A5D4E]">FREE</span></div>
              </div>

              <div className="mt-5 flex items-baseline justify-between border-t border-[#F2E8E6] pt-5">
                <span className="text-sm uppercase tracking-[0.15em] text-[#2C2B2C]">Total</span>
                <div className="text-right">
                  <span className="mr-1 text-[0.7rem] uppercase tracking-[0.12em] text-[#9A8E88]">AUD</span>
                  <span className="font-display text-3xl text-[#2C2B2C]" data-testid="summary-total">{fmt(grandTotal)}</span>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[#2C2B2C]" data-testid="checkout-afterpay-note">
                or 4 interest-free payments of <span className="font-semibold">{fmt(grandTotal / 4)}</span> with <AfterpayBadge />
              </div>

              <div className="mt-5 divide-y divide-[#F2E8E6] overflow-hidden rounded-2xl border border-[#F2E8E6] bg-gradient-to-b from-white to-[#FFFAF6]" data-testid="checkout-trust">
                {[
                  { icon: <Truck className="h-[18px] w-[18px]" strokeWidth={1.9} />, title: "Free express shipping", sub: "Tracked & dispatched within 1 business day" },
                  { icon: <Star className="h-[18px] w-[18px] fill-current" strokeWidth={1.9} />, title: "4.9★ · loved by 1,000+ women", sub: "Rated 5 stars by verified buyers" },
                  { icon: <ShieldCheck className="h-[18px] w-[18px]" strokeWidth={1.9} />, title: "30-night risk-free trial", sub: "Love your glow or get a full refund" },
                ].map((b) => (
                  <div key={b.title} className="group flex items-center gap-3.5 px-4 py-3.5 transition-colors hover:bg-[#FFF7F2]">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#FDEBE0] text-[#E6976F] ring-1 ring-inset ring-[#FBD9C6] transition-transform group-hover:scale-105">
                      {b.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[0.84rem] font-semibold leading-snug text-[#2C2B2C]">{b.title}</p>
                      <p className="text-xs leading-snug text-[#9A8E88]">{b.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 border-t border-[#F2E8E6] pt-5">
                <p className="mb-3 text-center text-[0.6rem] uppercase tracking-[0.18em] text-[#9A8E88]">Guaranteed safe checkout</p>
                <PaymentIcons className="justify-center" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Minimal checkout footer — policies */}
      <footer className="border-t border-[#F2E8E6] bg-white" data-testid="checkout-footer">
        <div className="mx-auto flex max-w-[1180px] flex-col items-center justify-between gap-3 px-5 py-6 text-center md:flex-row md:px-8 md:text-left">
          <p className="text-xs text-[#9A8E88]">© 2026 Aura Glow Wellness Pty Ltd · ABN 39 684 850 940</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[0.66rem] uppercase tracking-[0.14em] text-[#9A8E88]">
            <Link to="/policies/refund" data-testid="footer-refund" className="transition-colors hover:text-[#2C2B2C]">Refunds</Link>
            <span className="text-[#D8CCC2]">·</span>
            <Link to="/policies/shipping" data-testid="footer-shipping" className="transition-colors hover:text-[#2C2B2C]">Shipping</Link>
            <span className="text-[#D8CCC2]">·</span>
            <Link to="/policies/terms" data-testid="footer-terms" className="transition-colors hover:text-[#2C2B2C]">Terms</Link>
            <span className="text-[#D8CCC2]">·</span>
            <Link to="/policies/privacy" data-testid="footer-privacy" className="transition-colors hover:text-[#2C2B2C]">Privacy</Link>
            <span className="text-[#D8CCC2]">·</span>
            <Link to="/contact" data-testid="footer-contact" className="transition-colors hover:text-[#2C2B2C]">Contact us</Link>
          </div>
        </div>
      </footer>
    </div>
  );

  return paypal ? <PayPalScriptProvider options={ppOptions}>{content}</PayPalScriptProvider> : content;
}
